import { jsx, jsxs } from 'react/jsx-runtime';
import { useMutation } from '@tanstack/react-query';
import { isHotkey } from 'is-hotkey';
import { useRef, useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { CoverImage, CoverPicker } from './CoverImage.js';
import { IconPicker } from './IconPicker.js';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { useAnimatedText } from '../../../magic/hooks/useAnimatedText.js';
import { MagicRename } from '../../../magic/components/MagicRename.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { mediaMobile, mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { windowBlur } from '../../../utils/windowBlur.js';
import { SmallControlButton } from '@fabric-app/ui/components/Buttons/SmallControlButton';
import { P } from '@fabric-app/ui/components/Typography';
import Skeleton from '@fabric-app/ui/components/Skeleton';

const EditorialViewHeader = ({ title, description, coverImage, icon }) => {
  const hasIconSet = !!icon?.iconEl;
  const { mutate: _mutate, isPending } = useMutation({
    mutationFn: (file) => {
      if (coverImage?.onSave) {
        return coverImage?.onSave?.(file);
      }
      return Promise.resolve();
    }
  });
  const onFileSelect = coverImage?.onSave ? _mutate : void 0;
  return /* @__PURE__ */ jsxs(Container, {
    "data-multiselect-allow": true,
    children: [
      coverImage?.url ? /* @__PURE__ */ jsx(CoverImage, {
        url: coverImage?.url,
        onFileSelect,
        isUploading: isPending,
        onYOffsetPctChange: coverImage?.onYOffsetPctChange,
        yOffsetPct: coverImage?.yOffsetPct
      }) : hasIconSet ? /* @__PURE__ */ jsx(CoverVoid, {}) : null,
      /* @__PURE__ */ jsxs(TextContainer, {
        children: [
          /* @__PURE__ */ jsxs(IconAndControlsToolbar, {
            hasIcon: hasIconSet,
            children: [
              /* @__PURE__ */ jsx(IconPicker, {
                isDefault: icon?.isDefault,
                icon: icon?.iconEl,
                onSave: icon?.onSave
              }),
              coverImage && !coverImage.url && !!onFileSelect && /* @__PURE__ */ jsx(CoverPicker, {
                text: "Add cover",
                onFileSelect,
                isUploading: isPending
              })
            ]
          }),
          /* @__PURE__ */ jsx(Title, {
            placeholder: "Untitled",
            ...title
          }),
          description && /* @__PURE__ */ jsx(Description, {
            placeholder: `A description or any notes${title.defaultValue ? ` about ${title.defaultValue}` : ""}`,
            ...description
          })
        ]
      })
    ]
  });
};
const Description = ({ defaultValue, label, onSave, isLoading, ...rest }) => {
  return /* @__PURE__ */ jsxs(Flex, {
    direction: "column",
    children: [
      label && /* @__PURE__ */ jsx(P, {
        size: "sm",
        weight: 500,
        color: "tertiary",
        as: "div",
        children: label
      }),
      isLoading ? /* @__PURE__ */ jsx(Skeleton, {
        borderRadius: 8,
        height: 17,
        width: 330
      }) : !defaultValue && !onSave ? null : /* @__PURE__ */ jsx(ContentEditable, {
        onKeyDown: (e) => {
          if (isHotkey("mod+enter", e)) {
            e.preventDefault();
            e.stopPropagation();
            windowBlur();
          }
        },
        defaultValue,
        onSave,
        ...rest
      })
    ]
  });
};
const baseFloatingTextStyle = {
  fontFamily: "Manrope, sans-serif",
  fontWeight: 800,
  fontSize: "2rem",
  lineHeight: "2.75rem",
  minHeight: "2.75rem",
  color: `rgba(${cssVar["color-text-primary-rgb"]}, 0.5)`
};
const mobileFloatingTextStyle = {
  ...baseFloatingTextStyle,
  fontSize: "1.5rem",
  lineHeight: "2rem",
  minHeight: "2rem"
};
const Title = ({ defaultValue, onSave, icon, resourceId, onChangeDebounce = 500, isLoading, iconContainerProps, ...rest }) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const { isMobileView } = useResponsive();
  const inputControls = useInputControls(defaultValue, {
    onChange: onSave,
    debounce: onChangeDebounce,
    shouldUpdateStateOnDefaultValueChange: () => true
  });
  const { isAnimating, onAnimateText, addAnimation, inputStyle } = useAnimatedText({
    updateRealText: (title) => {
      inputControls.onChange(title);
    },
    floatingTextStyle: isMobileView ? mobileFloatingTextStyle : baseFloatingTextStyle
  });
  return /* @__PURE__ */ jsx(MagicRename, {
    resourceId: !onSave || isAnimating || isRenaming ? void 0 : resourceId,
    onRename: onAnimateText,
    onFetch: () => {
      setIsRenaming(true);
      windowBlur();
    },
    onSettled: () => setIsRenaming(false),
    children: /* @__PURE__ */ jsxs(Flex, {
      gap: 10,
      style: {
        position: "relative"
      },
      children: [
        icon && /* @__PURE__ */ jsx(TitleIconContainer, {
          ...iconContainerProps,
          children: icon
        }),
        isLoading ? /* @__PURE__ */ jsx(Skeleton, {
          borderRadius: 8,
          height: 44,
          width: 270
        }) : addAnimation(/* @__PURE__ */ jsx("div", {
          style: {
            overflow: "hidden"
          },
          children: /* @__PURE__ */ jsx(TitleTextArea, {
            inputControls,
            defaultValue,
            onSave,
            "data-testid": "view-title-input",
            style: inputStyle,
            onKeyDown: (e) => {
              if (isHotkey("enter", e)) {
                e.preventDefault();
                e.stopPropagation();
                windowBlur();
              }
            },
            ...rest
          })
        }))
      ]
    })
  });
};
const TextContainer = styled(Flex).attrs({
  direction: "column",
  gap: "elementsContainer"
}).withConfig({
  displayName: "EditorialViewHeader__TextContainer",
  componentId: "sc-f6f13401-0"
})([
  `padding:50px;padding-bottom:0;`,
  `{padding:22px;padding-bottom:12px;}position:relative;`
], mediaMobile);
const ICON_CONTAINER_SIZE = "3rem";
const CoverVoid = styled.div.withConfig({
  displayName: "EditorialViewHeader__CoverVoid",
  componentId: "sc-f6f13401-1"
})([
  `height:calc(50px + `,
  ` / 2);`
], ICON_CONTAINER_SIZE);
const ContentEditable = ({ defaultValue, onSave, onInput, onChangeDebounce = 500, ...rest }) => {
  const localInputControls = useInputControls(defaultValue || "", {
    onChange: onSave,
    debounce: onChangeDebounce
  });
  const inputControls = "inputControls" in rest ? rest.inputControls : localInputControls;
  const textAreaRef = useRef(null);
  useEffect(() => {
    if (textAreaRef.current) {
      if (inputControls.value === "") {
        textAreaRef.current.textContent = "";
      } else if (textAreaRef.current.textContent !== inputControls.value) {
        textAreaRef.current.textContent = inputControls.value || "";
      }
    }
  }, [
    inputControls.value
  ]);
  return /* @__PURE__ */ jsx(BaseContentEditable, {
    ref: textAreaRef,
    contentEditable: !!onSave,
    onInput: (e) => {
      onInput?.(e);
      inputControls.onChange(e.currentTarget.textContent || "");
    },
    onBlur: () => {
      onSave?.(inputControls.value);
    },
    ...rest
  });
};
const BaseContentEditable = styled.div.attrs((p) => {
  return {
    spellCheck: false,
    ...p
  };
}).withConfig({
  displayName: "EditorialViewHeader__BaseContentEditable",
  componentId: "sc-f6f13401-2"
})([
  `font-size:0.875rem;line-height:1.0625rem;min-height:1.0625rem;min-width:200px;background:transparent;resize:none;outline:none !important;color:`,
  `;width:max-content;max-width:100%;position:relative;&:not(:empty),&:focus-within,&:focus{&:before{display:none;}}&:before{content:attr(placeholder);pointer-events:none;color:`,
  `;}&[contenteditable='true']{`,
  `{&:hover{opacity:0.7;}}}`
], cssVar["color-text-placeholder"], cssVar["color-text-placeholder"], mediaHover);
const TitleTextArea = styled(ContentEditable).withConfig({
  displayName: "EditorialViewHeader__TitleTextArea",
  componentId: "sc-f6f13401-3"
})([
  `font-size:2rem;font-family:'Manrope',sans-serif;font-weight:800;line-height:2.75rem;min-height:2.75rem;`,
  `{font-size:1.5rem;line-height:2rem;min-height:2rem;}color:rgba(`,
  `,0.5);`
], mediaMobile, cssVar["color-text-primary-rgb"]);
const IconAndControlsToolbar = styled(Flex).attrs({
  direction: "row",
  gap: "element"
}).withConfig({
  displayName: "EditorialViewHeader__IconAndControlsToolbar",
  componentId: "sc-f6f13401-4"
})([
  `position:absolute;top:0;z-index:20;height:auto;display:flex;align-items:center;transform:translateY(-50%);gap:1.25rem;&:has(> [data-state='open']){opacity:1;}transition:0.2s opacity;`,
  `{transform:translateY(-100%);}`,
  `{opacity:0;}`
], mediaMobile, SmallControlButton);
const Container = styled(Flex).attrs({
  direction: "column",
  flexShrink: 0
}).withConfig({
  displayName: "EditorialViewHeader__Container",
  componentId: "sc-f6f13401-5"
})([
  `position:relative;&:focus-within,&:hover{`,
  `{opacity:1;}}`
], SmallControlButton);
const TitleIconContainer = styled.div.withConfig({
  displayName: "EditorialViewHeader__TitleIconContainer",
  componentId: "sc-f6f13401-6"
})([
  `width:2.25rem;height:2.25rem;font-size:2.25rem;margin-top:0.25rem;`
]);

export { EditorialViewHeader };
//# sourceMappingURL=EditorialViewHeader.js.map
